'use client';

import { c as _c } from "react/compiler-runtime";
import { type ClaimStreakResponse, useClaimAccount, useClaimMilestone, useClaimStreak, useEnsureRewardsParticipant, useGetMe, useGetRewardsContent, useGetRewardsLadder, useGetRewardsLeaderboard, useGetRewardsMe, useGetRewardsMilestones, useGetRewardsQuests, useGetRewardsReferrals, useGetRewardsStreak, useSubmitClip, useSubmitPost } from '@morfi/backend-sdk-v2/client';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { type ComponentProps, type ReactNode, useEffect, useRef, useState } from 'react';
import { ImpersonationBanner } from '@/components/impersonation/impersonation-banner';
import { MorfiLogo } from '@/components/logo';
import { ConnectProviderButtons } from '@/components/settings/connect-provider-buttons';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AppTabs } from '@/components/ui/app-tabs';
import { Icon } from '@/components/ui/icon';
import { Loading03Icon } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { REFERRAL_COOKIE } from '@/config/referral';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { NetworkLink } from '@/i18n/network-link';
import { getCookie } from '@/lib/config/cookies';
import { fireRewardBurst } from '@/lib/ui/confetti';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { ClaimAllMilestones } from './claim-all-milestones';
import { ClipCard } from './clip-card';
import { EmailCaptureBanner, EmailCaptureModal } from './email-capture';
import { InfoTipModal, useRewardsTips } from './info-tip';
import { LeaderboardPanel } from './leaderboard-panel';
import { LevelLadderModal } from './level-ladder';
import { MilestonesPanel } from './milestones-panel';
import { PostCard } from './post-card';
import { PrizeTermsModal } from './prize-terms-modal';
import { PrizeBanner, PrizeModal, ProfileCard } from './profile-card';
import { QuestsPanel } from './quests-panel';
import { ReferralsPanel } from './referrals-panel';
import { RewardsBackground } from './rewards-background';
import { RewardsProfile } from './rewards-profile';
import { getRewardsQueryEligibility } from './rewards-query-eligibility';
import { RewardsThemeToggle } from './rewards-theme-toggle';
import { RewardsUserMenu } from './rewards-user-menu';
import { buildRankShareIntent, buildRankShareUrl } from './share-rank';
import { TradingStreakPanel } from './trading-streak-panel';
import { useQuestVerification } from './use-quest-verification';
const ME_FIELDS = ['points', 'rank', 'level', 'streak', 'xConnected', 'xHandle', 'xName', 'avatar', 'telegramLinked', 'account', 'qualityScore', 'frozen', 'participantExists', 'joinedAt'] as const;

// The pending referrer code captured by middleware into the REFERRAL_CODE cookie
// (an X handle, or an in-flight UUID). Sent in the participant-create body so the
// backend can attribute the referral — the cookie lives on the frontend origin
// and is NOT sent cross-origin to the API, so it must ride the request body.
function referralVars(): {
  referralCode?: string;
} {
  const code = getCookie(REFERRAL_COOKIE);
  return {
    ...(code !== undefined && code.length > 0 ? {
      referralCode: code
    } : {})
  };
}

/** The join card offers exactly one provider: X, through the shared Turnkey-backed connect. */
const X_PROVIDER: readonly string[] = ['x'];
/** Fire the enroll-stuck watchdog if the auto-enroll spinner persists past this. */
const ENROLL_STUCK_MS = 15_000;
export function RewardsDashboard() {
  const $ = _c(230);
  const showProfile = useSearchParams().has("profile");
  const [tab, setTab] = useState("leaderboard");
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      fields: [...ME_FIELDS]
    };
    t1 = {
      refetchInterval: 20000
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  const me = useGetRewardsMe(t0, t1);
  const queryEligibility = getRewardsQueryEligibility({
    participantExists: me.data?.participantExists,
    showProfile,
    activeTab: tab
  });
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      fields: ["id", "title", "description", "points", "verifyMethod", "ctaMode", "actionUrl", "isNew", "status", "assurance"]
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== queryEligibility.quests) {
    t3 = {
      enabled: queryEligibility.quests
    };
    $[3] = queryEligibility.quests;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const quests = useGetRewardsQuests(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      fields: ["current", "longest", "level", "today", "timezone", "dayBoundaryLabel", "tradedToday", "claimedToday", "dailyPoints", "week"]
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== queryEligibility.streak) {
    t5 = {
      enabled: queryEligibility.streak
    };
    $[6] = queryEligibility.streak;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const streak = useGetRewardsStreak(t4, t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      fields: ["id", "title", "description", "track", "targetCount", "rewardPoints", "progress", "reached", "claimed", "claimedAt"]
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== queryEligibility.milestones) {
    t7 = {
      enabled: queryEligibility.milestones,
      refetchInterval: 20000
    };
    $[9] = queryEligibility.milestones;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const milestones = useGetRewardsMilestones(t6, t7);
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      fields: ["rows", "me"]
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== queryEligibility.board) {
    t9 = {
      enabled: queryEligibility.board,
      refetchInterval: 20000
    };
    $[12] = queryEligibility.board;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const board = useGetRewardsLeaderboard(t8, t9);
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      fields: ["id", "order", "key", "name", "minPoints"]
    };
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== queryEligibility.ladder) {
    t11 = {
      enabled: queryEligibility.ladder
    };
    $[15] = queryEligibility.ladder;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const ladder = useGetRewardsLadder(t10, t11);
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      fields: ["link", "code", "referred", "l1Total", "l2Total", "overrideTodayRemaining"]
    };
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== queryEligibility.referrals) {
    t13 = {
      enabled: queryEligibility.referrals,
      refetchInterval: 20000
    };
    $[18] = queryEligibility.referrals;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const referrals = useGetRewardsReferrals(t12, t13);
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      fields: ["submissions", "capsRemaining"]
    };
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== queryEligibility.content) {
    t15 = {
      enabled: queryEligibility.content
    };
    $[21] = queryEligibility.content;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const content = useGetRewardsContent(t14, t15);
  const ensure = useEnsureRewardsParticipant();
  const claimStreak = useClaimStreak();
  const claimAccount = useClaimAccount();
  const claimMilestone = useClaimMilestone();
  const submitPost = useSubmitPost();
  const submitClip = useSubmitClip();
  const tips = useRewardsTips();
  const t = useTranslations("rewards");
  const tShare = useTranslations("share.rewards_rank");
  const [prizeOpen, setPrizeOpen] = useState(false);
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      fields: ["emailVerified"]
    };
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const accountMe = useGetMe(t16);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailJustVerified, setEmailJustVerified] = useState(false);
  const needsEmailCapture = accountMe.data?.emailVerified === false && !emailJustVerified;
  const [prizeTermsOpen, setPrizeTermsOpen] = useState(false);
  const [ladderOpen, setLadderOpen] = useState(false);
  const [claimingMilestone, setClaimingMilestone] = useState(null);
  const [connectError, setConnectError] = useState(null);
  const m = me.data;
  let t17;
  if ($[24] !== board || $[25] !== me) {
    t17 = async () => {
      await Promise.all([me.refetch(), board.refetch()]);
    };
    $[24] = board;
    $[25] = me;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const refreshCore = t17;
  let t18;
  if ($[27] !== m?.level?.key || $[28] !== m?.level?.name || $[29] !== m?.points || $[30] !== m?.rank || $[31] !== m?.xHandle || $[32] !== quests.rows || $[33] !== tShare) {
    const shareIntent = buildRankShareIntent(buildRankShareUrl({
      rank: m?.rank ?? null,
      points: m?.points.current ?? "0",
      handle: m?.xHandle ?? null,
      levelKey: m?.level?.key ?? null,
      levelName: m?.level?.name ?? null
    }), tShare("text", {
      rank: m?.rank ?? 0,
      points: m?.points.current ?? "0"
    }));
    t18 = ((quests.rows ?? []) as ComponentProps<typeof QuestsPanel>['quests']).map(q => q.verifyMethod === 10 ? {
      ...q,
      actionUrl: shareIntent
    } : q);
    $[27] = m?.level?.key;
    $[28] = m?.level?.name;
    $[29] = m?.points;
    $[30] = m?.rank;
    $[31] = m?.xHandle;
    $[32] = quests.rows;
    $[33] = tShare;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const questRows = t18;
  let t19;
  if ($[35] !== milestones || $[36] !== quests || $[37] !== refreshCore) {
    t19 = () => Promise.all([quests.refetch(), milestones.refetch(), refreshCore()]);
    $[35] = milestones;
    $[36] = quests;
    $[37] = refreshCore;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const questVerify = useQuestVerification(questRows, t19);
  let onClaimMilestone;
  let refreshMilestones;
  if ($[39] !== claimMilestone || $[40] !== milestones || $[41] !== refreshCore) {
    onClaimMilestone = (id, origin) => {
      setClaimingMilestone(id);
      claimMilestone.mutate({
        id
      }, {
        onSuccess: () => {
          onEvent("rewards_milestone_claimed", {
            milestoneId: id
          });
          fireRewardBurst(origin);
        },
        onSettled: () => {
          setClaimingMilestone(null);
          refreshMilestones();
        }
      });
    };
    refreshMilestones = () => {
      milestones.refetch();
      refreshCore();
    };
    $[39] = claimMilestone;
    $[40] = milestones;
    $[41] = refreshCore;
    $[42] = onClaimMilestone;
    $[43] = refreshMilestones;
  } else {
    onClaimMilestone = $[42];
    refreshMilestones = $[43];
  }
  let t20;
  if ($[44] !== ensure || $[45] !== me || $[46] !== milestones || $[47] !== quests || $[48] !== t) {
    t20 = () => {
      const vars = referralVars();
      ensure.mutate(vars, {
        onSuccess: () => {
          onEvent("rewards_participant_created", {
            referred: vars.referralCode !== undefined,
            viaAutoEnroll: false
          });
          Promise.all([me.refetch(), quests.refetch(), milestones.refetch()]);
        },
        onError: e => setConnectError(e.message || t("join.couldNotStart"))
      });
    };
    $[44] = ensure;
    $[45] = me;
    $[46] = milestones;
    $[47] = quests;
    $[48] = t;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  const createParticipant = t20;
  let t21;
  if ($[50] !== createParticipant) {
    t21 = () => {
      setConnectError(null);
      createParticipant();
    };
    $[50] = createParticipant;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  const handleStart = t21;
  let t22;
  if ($[52] !== createParticipant || $[53] !== me) {
    t22 = async () => {
      onEvent("rewards_x_connect_succeeded", {});
      setConnectError(null);
      await me.refetch();
      createParticipant();
    };
    $[52] = createParticipant;
    $[53] = me;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  const handleXConnected = t22;
  const autoEnrolledRef = useRef(false);
  let t23;
  if ($[55] !== ensure || $[56] !== m?.participantExists || $[57] !== m?.xConnected || $[58] !== me || $[59] !== milestones || $[60] !== quests || $[61] !== t) {
    t23 = () => {
      if (autoEnrolledRef.current) {
        return;
      }
      if (m?.xConnected !== true || m?.participantExists !== false) {
        return;
      }
      if (ensure.isPending || ensure.isSuccess) {
        return;
      }
      autoEnrolledRef.current = true;
      const vars_0 = referralVars();
      ensure.mutate(vars_0, {
        onSuccess: () => {
          onEvent("rewards_participant_created", {
            referred: vars_0.referralCode !== undefined,
            viaAutoEnroll: true
          });
          Promise.all([me.refetch(), quests.refetch(), milestones.refetch()]);
        },
        onError: e_0 => {
          autoEnrolledRef.current = false;
          setConnectError(e_0.message || t("join.couldNotStart"));
        }
      });
    };
    $[55] = ensure;
    $[56] = m?.participantExists;
    $[57] = m?.xConnected;
    $[58] = me;
    $[59] = milestones;
    $[60] = quests;
    $[61] = t;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  const t24 = m?.xConnected;
  const t25 = m?.participantExists;
  let t26;
  if ($[63] !== ensure.isPending || $[64] !== ensure.isSuccess || $[65] !== ensure.mutate || $[66] !== me.refetch || $[67] !== milestones.refetch || $[68] !== quests.refetch || $[69] !== t || $[70] !== t24 || $[71] !== t25) {
    t26 = [t24, t25, ensure.mutate, ensure.isPending, ensure.isSuccess, me.refetch, quests.refetch, milestones.refetch, t];
    $[63] = ensure.isPending;
    $[64] = ensure.isSuccess;
    $[65] = ensure.mutate;
    $[66] = me.refetch;
    $[67] = milestones.refetch;
    $[68] = quests.refetch;
    $[69] = t;
    $[70] = t24;
    $[71] = t25;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  useEffect(t23, t26);
  const pageViewedRef = useRef(null);
  let t27;
  let t28;
  if ($[73] !== m || $[74] !== showProfile) {
    t27 = () => {
      if (!m) {
        return;
      }
      if (pageViewedRef.current === null) {
        pageViewedRef.current = new Set();
      }
      const seen = pageViewedRef.current;
      const view = showProfile ? "profile" : "dashboard";
      if (seen.has(view)) {
        return;
      }
      seen.add(view);
      onEvent("rewards_page_viewed", {
        view,
        participantExists: m.participantExists,
        xConnected: m.xConnected
      });
    };
    t28 = [m, showProfile];
    $[73] = m;
    $[74] = showProfile;
    $[75] = t27;
    $[76] = t28;
  } else {
    t27 = $[75];
    t28 = $[76];
  }
  useEffect(t27, t28);
  const enrollSpinnerShown = m?.xConnected === true && m?.participantExists === false && connectError === null;
  let t29;
  let t30;
  if ($[77] !== enrollSpinnerShown) {
    t29 = () => {
      if (!enrollSpinnerShown) {
        return;
      }
      const startedAt = Date.now();
      const timer = setTimeout(() => {
        onEvent("rewards_enroll_stuck", {
          ms: Date.now() - startedAt
        });
      }, ENROLL_STUCK_MS);
      return () => clearTimeout(timer);
    };
    t30 = [enrollSpinnerShown];
    $[77] = enrollSpinnerShown;
    $[78] = t29;
    $[79] = t30;
  } else {
    t29 = $[78];
    t30 = $[79];
  }
  useEffect(t29, t30);
  if (me.isError) {
    let t31;
    if ($[80] !== t) {
      t31 = t("dashboard.loadError");
      $[80] = t;
      $[81] = t31;
    } else {
      t31 = $[81];
    }
    let t32;
    if ($[82] !== t31) {
      t32 = <p className="text-body text-foreground-secondary">{t31}</p>;
      $[82] = t31;
      $[83] = t32;
    } else {
      t32 = $[83];
    }
    let t33;
    if ($[84] !== me) {
      t33 = () => void me.refetch();
      $[84] = me;
      $[85] = t33;
    } else {
      t33 = $[85];
    }
    let t34;
    if ($[86] !== t) {
      t34 = t("dashboard.retry");
      $[86] = t;
      $[87] = t34;
    } else {
      t34 = $[87];
    }
    let t35;
    if ($[88] !== t33 || $[89] !== t34) {
      t35 = <AnimatedButton size="sm" onClick={t33}>{t34}</AnimatedButton>;
      $[88] = t33;
      $[89] = t34;
      $[90] = t35;
    } else {
      t35 = $[90];
    }
    let t36;
    if ($[91] !== t32 || $[92] !== t35) {
      t36 = <div className="flex flex-1 items-center justify-center lg:min-h-0"><Panel className="w-full max-w-md"><PanelContent className="flex flex-col items-center gap-4 py-8 text-center">{t32}{t35}</PanelContent></Panel></div>;
      $[91] = t32;
      $[92] = t35;
      $[93] = t36;
    } else {
      t36 = $[93];
    }
    let t37;
    if ($[94] !== showProfile || $[95] !== t36) {
      t37 = <RewardsShell points="0" handle={null} userId={null} avatarUrl={null} showProfile={showProfile}>{t36}</RewardsShell>;
      $[94] = showProfile;
      $[95] = t36;
      $[96] = t37;
    } else {
      t37 = $[96];
    }
    return t37;
  }
  if (!m) {
    let t31;
    if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = <RewardsLoadingBody />;
      $[97] = t31;
    } else {
      t31 = $[97];
    }
    let t32;
    if ($[98] !== showProfile) {
      t32 = <RewardsShell points="0" handle={null} userId={null} avatarUrl={null} showProfile={showProfile}>{t31}</RewardsShell>;
      $[98] = showProfile;
      $[99] = t32;
    } else {
      t32 = $[99];
    }
    return t32;
  }
  if (!m.participantExists) {
    if (m.xConnected && connectError === null) {
      let t31;
      if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
        t31 = <RewardsLoadingBody />;
        $[100] = t31;
      } else {
        t31 = $[100];
      }
      let t32;
      if ($[101] !== m.account.identity || $[102] !== m.avatar || $[103] !== m.points.current || $[104] !== m.xHandle || $[105] !== showProfile) {
        t32 = <RewardsShell points={m.points.current} handle={m.xHandle} userId={m.account.identity} avatarUrl={m.avatar} showProfile={showProfile}>{t31}</RewardsShell>;
        $[101] = m.account.identity;
        $[102] = m.avatar;
        $[103] = m.points.current;
        $[104] = m.xHandle;
        $[105] = showProfile;
        $[106] = t32;
      } else {
        t32 = $[106];
      }
      return t32;
    }
    const busy = ensure.isPending;
    const t31 = m.points.current;
    const t32 = m.xHandle;
    const t33 = m.account.identity;
    const t34 = m.avatar;
    let t35;
    if ($[107] !== needsEmailCapture) {
      t35 = needsEmailCapture ? <EmailCaptureBanner onOpen={() => setEmailModalOpen(true)} /> : undefined;
      $[107] = needsEmailCapture;
      $[108] = t35;
    } else {
      t35 = $[108];
    }
    let t36;
    if ($[109] !== t) {
      t36 = t("join.title");
      $[109] = t;
      $[110] = t36;
    } else {
      t36 = $[110];
    }
    let t37;
    if ($[111] !== t36) {
      t37 = <h1 className="text-heading-lg font-medium text-foreground-primary">{t36}</h1>;
      $[111] = t36;
      $[112] = t37;
    } else {
      t37 = $[112];
    }
    let t38;
    if ($[113] !== m.xConnected || $[114] !== t) {
      t38 = m.xConnected ? t("join.subtitleConnected") : t("join.subtitleDisconnected");
      $[113] = m.xConnected;
      $[114] = t;
      $[115] = t38;
    } else {
      t38 = $[115];
    }
    let t39;
    if ($[116] !== t38) {
      t39 = <p className="text-body text-foreground-secondary">{t38}</p>;
      $[116] = t38;
      $[117] = t39;
    } else {
      t39 = $[117];
    }
    let t40;
    if ($[118] !== busy || $[119] !== handleStart || $[120] !== handleXConnected || $[121] !== m.xConnected || $[122] !== t) {
      t40 = m.xConnected ? <AnimatedButton data-testid="join-btn" disabled={busy} loading={busy} onClick={handleStart}>{busy ? t("join.connecting") : t("join.startEarning")}</AnimatedButton> : <ConnectProviderButtons providers={X_PROVIDER} hideConnected={true} prominent={true} onConnected={() => {
        handleXConnected();
      }} />;
      $[118] = busy;
      $[119] = handleStart;
      $[120] = handleXConnected;
      $[121] = m.xConnected;
      $[122] = t;
      $[123] = t40;
    } else {
      t40 = $[123];
    }
    let t41;
    if ($[124] !== connectError) {
      t41 = connectError !== null ? <p className="text-caption text-feedback-error-content" role="alert">{connectError}</p> : null;
      $[124] = connectError;
      $[125] = t41;
    } else {
      t41 = $[125];
    }
    let t42;
    if ($[126] !== t37 || $[127] !== t39 || $[128] !== t40 || $[129] !== t41) {
      t42 = <div className="flex flex-1 items-center justify-center lg:min-h-0"><Panel tone="brand" className="w-full max-w-md"><PanelContent className="flex flex-col items-center gap-4 py-8 text-center">{t37}{t39}{t40}{t41}</PanelContent></Panel></div>;
      $[126] = t37;
      $[127] = t39;
      $[128] = t40;
      $[129] = t41;
      $[130] = t42;
    } else {
      t42 = $[130];
    }
    let t43;
    if ($[131] !== m.account.identity || $[132] !== m.avatar || $[133] !== m.points.current || $[134] !== m.xHandle || $[135] !== showProfile || $[136] !== t35 || $[137] !== t42) {
      t43 = <RewardsShell points={t31} handle={t32} userId={t33} avatarUrl={t34} showProfile={showProfile} banner={t35}>{t42}</RewardsShell>;
      $[131] = m.account.identity;
      $[132] = m.avatar;
      $[133] = m.points.current;
      $[134] = m.xHandle;
      $[135] = showProfile;
      $[136] = t35;
      $[137] = t42;
      $[138] = t43;
    } else {
      t43 = $[138];
    }
    return t43;
  }
  const caps = content.data?.capsRemaining;
  const t31 = referrals.data?.referred.length ?? 0;
  let t32;
  if ($[139] !== m.qualityScore || $[140] !== m.streak.current || $[141] !== m.streak.longest || $[142] !== t31) {
    t32 = {
      streakCurrent: m.streak.current,
      streakLongest: m.streak.longest,
      referrals: t31,
      score: m.qualityScore
    };
    $[139] = m.qualityScore;
    $[140] = m.streak.current;
    $[141] = m.streak.longest;
    $[142] = t31;
    $[143] = t32;
  } else {
    t32 = $[143];
  }
  const profileKpis = t32;
  let t33;
  if ($[144] !== content || $[145] !== t) {
    t33 = kind => {
      onEvent("rewards_content_submitted", {
        kind
      });
      content.refetch();
      fireRewardBurst();
      showToast({
        id: TOAST_IDS.REWARDS_CONTENT_SUBMITTED,
        message: t("submit.underReview"),
        variant: "success"
      });
    };
    $[144] = content;
    $[145] = t;
    $[146] = t33;
  } else {
    t33 = $[146];
  }
  const onContentResult = t33;
  let t34;
  if ($[147] !== t) {
    t34 = data => {
      onEvent("rewards_streak_claimed", {
        milestone: data.milestoneBonus > 0
      });
      fireRewardBurst();
      showToast({
        id: TOAST_IDS.REWARDS_STREAK_CLAIMED,
        message: t("streak.claimedToast", {
          points: data.awarded + data.milestoneBonus
        }),
        variant: "success"
      });
    };
    $[147] = t;
    $[148] = t34;
  } else {
    t34 = $[148];
  }
  const onStreakClaimed = t34;
  let t35;
  if ($[149] !== t) {
    t35 = t("tabs.leaderboard");
    $[149] = t;
    $[150] = t35;
  } else {
    t35 = $[150];
  }
  let t36;
  if ($[151] !== t35) {
    t36 = {
      key: "leaderboard",
      label: t35
    };
    $[151] = t35;
    $[152] = t36;
  } else {
    t36 = $[152];
  }
  let t37;
  if ($[153] !== t) {
    t37 = t("tabs.milestones");
    $[153] = t;
    $[154] = t37;
  } else {
    t37 = $[154];
  }
  let t38;
  if ($[155] !== t37) {
    t38 = {
      key: "milestones",
      label: t37
    };
    $[155] = t37;
    $[156] = t38;
  } else {
    t38 = $[156];
  }
  let t39;
  if ($[157] !== t36 || $[158] !== t38) {
    t39 = [t36, t38];
    $[157] = t36;
    $[158] = t38;
    $[159] = t39;
  } else {
    t39 = $[159];
  }
  let t40;
  if ($[160] !== setTab || $[161] !== t39 || $[162] !== tab) {
    t40 = <SegmentedControl items={t39} active={tab} onChange={setTab} layoutId="rewards-board-toggle" variant="chart-filled" size="md" fullWidth={true} className="min-w-0 flex-1" asTabs={true} />;
    $[160] = setTab;
    $[161] = t39;
    $[162] = tab;
    $[163] = t40;
  } else {
    t40 = $[163];
  }
  let t41;
  if ($[164] !== tips.board) {
    t41 = <InfoTipModal tip={tips.board} />;
    $[164] = tips.board;
    $[165] = t41;
  } else {
    t41 = $[165];
  }
  let t42;
  if ($[166] !== t40 || $[167] !== t41) {
    t42 = <div className="flex items-center gap-2">{t40}{t41}</div>;
    $[166] = t40;
    $[167] = t41;
    $[168] = t42;
  } else {
    t42 = $[168];
  }
  const boardHeader = t42;
  let t43;
  if ($[169] !== needsEmailCapture) {
    t43 = needsEmailCapture ? <EmailCaptureBanner onOpen={() => setEmailModalOpen(true)} /> : undefined;
    $[169] = needsEmailCapture;
    $[170] = t43;
  } else {
    t43 = $[170];
  }
  let t44;
  if ($[171] !== board.data || $[172] !== boardHeader || $[173] !== caps?.clip || $[174] !== caps?.post || $[175] !== claimAccount || $[176] !== claimStreak || $[177] !== claimingMilestone || $[178] !== content.data?.submissions || $[179] !== ladder.rows || $[180] !== m || $[181] !== milestones || $[182] !== needsEmailCapture || $[183] !== onClaimMilestone || $[184] !== onContentResult || $[185] !== onStreakClaimed || $[186] !== profileKpis || $[187] !== questRows || $[188] !== questVerify || $[189] !== referrals.data || $[190] !== refreshCore || $[191] !== refreshMilestones || $[192] !== setTab || $[193] !== showProfile || $[194] !== streak || $[195] !== submitClip || $[196] !== submitPost || $[197] !== tab) {
    t44 = showProfile ? <RewardsProfile me={m} kpis={profileKpis} referrals={(referrals.data ?? undefined) as ComponentProps<typeof RewardsProfile>['referrals']} submissions={(content.data?.submissions ?? undefined) as ComponentProps<typeof RewardsProfile>['submissions']} /> : <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:[grid-template-rows:auto_minmax(0,1fr)] lg:min-h-0 lg:flex-1 xl:grid-cols-3"><div className="flex flex-col gap-3 md:col-start-1 md:row-start-1 lg:min-h-0 xl:col-start-1"><PrizeBanner onOpen={() => setPrizeOpen(true)} /><ProfileCard me={m} onCreateAccount={() => claimAccount.mutate({}, {
          onSuccess: _temp,
          onSettled: () => void refreshCore()
        })} onOpenLevelLadder={() => setLadderOpen(true)} creating={claimAccount.isPending} /></div><div className="flex flex-col gap-3 md:col-start-2 md:row-start-1 md:row-span-2 lg:min-h-0 xl:col-start-2 xl:col-span-2"><div className="grid grid-cols-1 gap-3 xl:grid-cols-2"><TradingStreakPanel streak={streak.data ?? undefined} onClaim={() => claimStreak.mutate({}, {
            onSuccess: onStreakClaimed,
            onSettled: () => {
              streak.refetch();
              milestones.refetch();
              refreshCore();
            }
          })} claiming={claimStreak.isPending} /><PostCard capsRemaining={caps?.post} referralLink={referrals.data?.link} onSubmit={v => submitPost.mutate({
            tweetUrlOrId: v
          }, {
            onSuccess: () => onContentResult("post")
          })} busy={submitPost.isPending} /><ClipCard capsRemaining={caps?.clip} referralLink={referrals.data?.link} onSubmit={v_0 => submitClip.mutate({
            tweetUrlOrId: v_0
          }, {
            onSuccess: () => onContentResult("clip")
          })} busy={submitClip.isPending} /><ReferralsPanel referrals={(referrals.data ?? undefined) as ComponentProps<typeof ReferralsPanel>['referrals']} /></div><QuestsPanel quests={questRows} verify={questVerify} onQuestAction={q_0 => {
          if (q_0.verifyMethod === 11 && needsEmailCapture) {
            setEmailModalOpen(true);
            return true;
          }
          return false;
        }} className="lg:min-h-0 lg:flex-1" /></div><div className="flex flex-col md:col-start-1 md:row-start-2 lg:min-h-0 lg:flex-1 xl:col-start-1"><AppTabs.Root value={tab} onValueChange={setTab} className="flex flex-col lg:min-h-0 lg:flex-1"><AppTabs.Content value="leaderboard" className="flex flex-col lg:min-h-0 lg:flex-1"><LeaderboardPanel headerSlot={boardHeader} board={(board.data ?? undefined) as ComponentProps<typeof LeaderboardPanel>['board']} viewerUserId={m?.account.identity ?? null} viewerHandle={m?.xHandle ?? null} viewerAvatar={m?.avatar ?? null} ladder={(ladder.rows ?? []) as ComponentProps<typeof LeaderboardPanel>['ladder']} onOpenLadder={() => setLadderOpen(true)} className="lg:min-h-0 lg:flex-1" /></AppTabs.Content><AppTabs.Content value="milestones" className="flex flex-col lg:min-h-0 lg:flex-1"><MilestonesPanel headerSlot={boardHeader} milestones={(milestones.rows ?? []) as ComponentProps<typeof MilestonesPanel>['milestones']} onClaim={onClaimMilestone} claimingId={claimingMilestone} claimAllSlot={<ClaimAllMilestones milestones={milestones.rows ?? []} onSettled={refreshMilestones} />} className="lg:min-h-0 lg:flex-1" /></AppTabs.Content></AppTabs.Root></div></div>;
    $[171] = board.data;
    $[172] = boardHeader;
    $[173] = caps?.clip;
    $[174] = caps?.post;
    $[175] = claimAccount;
    $[176] = claimStreak;
    $[177] = claimingMilestone;
    $[178] = content.data?.submissions;
    $[179] = ladder.rows;
    $[180] = m;
    $[181] = milestones;
    $[182] = needsEmailCapture;
    $[183] = onClaimMilestone;
    $[184] = onContentResult;
    $[185] = onStreakClaimed;
    $[186] = profileKpis;
    $[187] = questRows;
    $[188] = questVerify;
    $[189] = referrals.data;
    $[190] = refreshCore;
    $[191] = refreshMilestones;
    $[192] = setTab;
    $[193] = showProfile;
    $[194] = streak;
    $[195] = submitClip;
    $[196] = submitPost;
    $[197] = tab;
    $[198] = t44;
  } else {
    t44 = $[198];
  }
  let t45;
  if ($[199] !== m.account.identity || $[200] !== m.avatar || $[201] !== m.points.current || $[202] !== m.xHandle || $[203] !== showProfile || $[204] !== t43 || $[205] !== t44) {
    t45 = <RewardsShell points={m.points.current} handle={m.xHandle} userId={m.account.identity} avatarUrl={m.avatar} showProfile={showProfile} banner={t43}>{t44}</RewardsShell>;
    $[199] = m.account.identity;
    $[200] = m.avatar;
    $[201] = m.points.current;
    $[202] = m.xHandle;
    $[203] = showProfile;
    $[204] = t43;
    $[205] = t44;
    $[206] = t45;
  } else {
    t45 = $[206];
  }
  let t46;
  if ($[207] !== ladder.rows) {
    t46 = ladder.rows ?? [];
    $[207] = ladder.rows;
    $[208] = t46;
  } else {
    t46 = $[208];
  }
  const t47 = t46 as ComponentProps<typeof LevelLadderModal>['ladder'];
  const t48 = m?.points.lifetime ?? "0";
  let t49;
  if ($[209] !== ladderOpen || $[210] !== t47 || $[211] !== t48) {
    t49 = <LevelLadderModal open={ladderOpen} onOpenChange={setLadderOpen} ladder={t47} currentLifetime={t48} />;
    $[209] = ladderOpen;
    $[210] = t47;
    $[211] = t48;
    $[212] = t49;
  } else {
    t49 = $[212];
  }
  let t50;
  if ($[213] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = () => {
      setPrizeOpen(false);
      setPrizeTermsOpen(true);
    };
    $[213] = t50;
  } else {
    t50 = $[213];
  }
  let t51;
  if ($[214] !== prizeOpen) {
    t51 = <PrizeModal open={prizeOpen} onOpenChange={setPrizeOpen} onOpenTerms={t50} />;
    $[214] = prizeOpen;
    $[215] = t51;
  } else {
    t51 = $[215];
  }
  let t52;
  if ($[216] !== prizeTermsOpen) {
    t52 = <PrizeTermsModal open={prizeTermsOpen} onOpenChange={setPrizeTermsOpen} />;
    $[216] = prizeTermsOpen;
    $[217] = t52;
  } else {
    t52 = $[217];
  }
  let t53;
  if ($[218] !== accountMe || $[219] !== questVerify) {
    t53 = () => {
      setEmailJustVerified(true);
      accountMe.refetch();
      questVerify.reverify("add_email");
    };
    $[218] = accountMe;
    $[219] = questVerify;
    $[220] = t53;
  } else {
    t53 = $[220];
  }
  let t54;
  if ($[221] !== emailModalOpen || $[222] !== t53) {
    t54 = <EmailCaptureModal open={emailModalOpen} onOpenChange={setEmailModalOpen} onVerified={t53} />;
    $[221] = emailModalOpen;
    $[222] = t53;
    $[223] = t54;
  } else {
    t54 = $[223];
  }
  let t55;
  if ($[224] !== t45 || $[225] !== t49 || $[226] !== t51 || $[227] !== t52 || $[228] !== t54) {
    t55 = <>{t45}{t49}{t51}{t52}{t54}</>;
    $[224] = t45;
    $[225] = t49;
    $[226] = t51;
    $[227] = t52;
    $[228] = t54;
    $[229] = t55;
  } else {
    t55 = $[229];
  }
  return t55;
}

/**
 * Stable page shell (ambient background + header) shared by the loading, join,
 * and dashboard states so a sign-in / data load doesn't shift the layout — only
 * the body inside swaps (MOR-783). The header tolerates placeholder identity
 * (points "0", null handle/avatar) so it stays layout-stable before /me lands.
 */
function _temp() {
  return onEvent("rewards_account_created", {});
}
function RewardsShell(t0) {
  const $ = _c(31);
  const {
    points,
    handle,
    userId,
    avatarUrl,
    showProfile,
    banner,
    children
  } = t0;
  const t = useTranslations("rewards");
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <RewardsBackground />;
    t2 = <ImpersonationBanner />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("mx-auto flex w-full max-w-6xl flex-col px-4 py-5 lg:min-h-0 lg:flex-1");
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t) {
    t4 = t("userMenu.markets");
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <MorfiLogo size={26} />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4) {
    t6 = <NetworkLink href="/markets" aria-label={t4} className="flex items-center">{t5}</NetworkLink>;
    $[6] = t4;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t) {
    t7 = t("badge");
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = <span className="rounded-pill border px-2 py-0.5 text-micro font-medium tracking-[0.04em] text-foreground-tertiary">{t7}</span>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t6 || $[13] !== t8) {
    t9 = <div className="flex items-center gap-2">{t6}{t8}</div>;
    $[12] = t6;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <RewardsThemeToggle />;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = showProfile ? "profile" : "dashboard";
  let t12;
  if ($[16] !== avatarUrl || $[17] !== handle || $[18] !== points || $[19] !== t11 || $[20] !== userId) {
    t12 = <div className="flex items-center gap-2">{t10}<RewardsUserMenu points={points} handle={handle} userId={userId} avatarUrl={avatarUrl} active={t11} /></div>;
    $[16] = avatarUrl;
    $[17] = handle;
    $[18] = points;
    $[19] = t11;
    $[20] = userId;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t9) {
    t13 = <header data-testid="rewards-header" className="mb-4 flex items-center justify-between gap-4">{t9}{t12}</header>;
    $[22] = t12;
    $[23] = t9;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== children || $[26] !== t13) {
    t14 = <div className={t3}>{t13}{children}</div>;
    $[25] = children;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== banner || $[29] !== t14) {
    t15 = <main data-testid="rewards-page" className="relative isolate flex min-h-dvh flex-col bg-background-secondary text-foreground-primary [@media(min-width:1024px)_and_(min-height:950px)]:h-dvh [@media(min-width:1024px)_and_(min-height:950px)]:overflow-hidden">{t1}{t2}{banner}{t14}</main>;
    $[28] = banner;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}

/** Centered spinner that fills the same body region the bento occupies, so the
 *  spinner → content swap inside {@link RewardsShell} doesn't move the chrome. */
function RewardsLoadingBody() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div data-testid="rewards-loading" className="flex flex-1 items-center justify-center lg:min-h-0"><Icon icon={Loading03Icon} className="size-10 animate-spin text-foreground-secondary motion-reduce:animate-none" aria-hidden={true} /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}